import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { singularityStore } from "@/store/store";
import * as t from "io-ts";
import { SiteStateV } from "@/futurelayer/settings/types";
import { isRight } from "fp-ts/lib/Either";
import { jsonParseOrUndefined } from "@/util/json-parse-or-undefined";
import { getFutureLayerAppUrl } from "../app/app";

const JWTResponseV = t.type({
  token: t.string,
  is_anonymous: t.union([t.boolean, t.undefined])
});

const JWT_EXPIRY_BUFFER_MS = 30 * 1000; // 30 seconds buffer before expiry

/**
 * Extract and store site state from response header
 */
function extractAndStoreSiteState(response: Response): void {
  console.log("extractAndStoreSiteState: Checking for X-Site-State header");
  console.log("extractAndStoreSiteState: Response status:", response.status);
  console.log("extractAndStoreSiteState: Response URL:", response.url);

  // Log all headers
  const allHeaders: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    allHeaders[key] = value;
  });
  console.log("extractAndStoreSiteState: All response headers:", allHeaders);

  const siteStateHeader = response.headers.get("X-Site-State");
  console.log("extractAndStoreSiteState: X-Site-State header value:", siteStateHeader);

  if (!siteStateHeader) {
    console.log("extractAndStoreSiteState: No X-Site-State header found");
    return;
  }

  const parsed = jsonParseOrUndefined(siteStateHeader);
  if (parsed === undefined) {
    console.error("extractAndStoreSiteState: Failed to parse X-Site-State header");
    return;
  }

  console.log("extractAndStoreSiteState: Parsed site state:", parsed);

  const validated = SiteStateV.decode(parsed);

  if (isRight(validated)) {
    console.log("extractAndStoreSiteState: Valid site state, storing in store:", validated.right);

    // Store in Vuex store
    singularityStore.setSiteStatus({
      ...singularityStore.siteStatus,
      siteState: validated.right
    });

    // Save to WordPress database (fire and forget)
    makeWordPressAjaxRequest({
      action: "futurelayer_update_site_state",
      dataToPost: { site_state: siteStateHeader },
      decoder: t.type({
        success: t.boolean,
        message: t.string
      }),
      suppressErrorLogging: true,
      friendlyName: "Update Site State"
    }).catch((error) => {
      console.error("Failed to save site state to database:", error);
    });
  } else {
    console.error("extractAndStoreSiteState: Invalid site state format:", validated.left);
  }
}

/**
 * Check if the current JWT token is valid and not expired
 */
export function isJWTValid(): boolean {
  if (!singularityStore.aiServer) {
    throw new Error("no FutureLayer aiServer");
  }

  const { jwt, jwtExpiresAt } = singularityStore.aiServer;

  console.log("JWT: Checking token validity", {
    hasToken: !!jwt,
    expiresAt: jwtExpiresAt,
    now: Date.now(),
    isValid: jwt && jwtExpiresAt ? Date.now() < (jwtExpiresAt - JWT_EXPIRY_BUFFER_MS) : false
  });

  if (!jwt || !jwtExpiresAt) {
    return false;
  }

  // Check if token expires within the buffer time
  return Date.now() < (jwtExpiresAt - JWT_EXPIRY_BUFFER_MS);
}

/**
 * Fetch a new JWT token from the WordPress backend
 */
export async function fetchJWTToken(): Promise<string | null> {
  
  if (!singularityStore.aiServer) {
    throw new Error("no FutureLayer aiServer");
  }

  console.log("JWT: Fetching new token from WordPress backend");

  const result = await makeWordPressAjaxRequest({
    action: "futurelayer_get_token",
    decoder: JWTResponseV,
    suppressErrorLogging: false,
    friendlyName: "Fetch FutureLayer JWT Token"
  });

  if (result._tag === "Right") {
    const token = result.right.token;
    const isAnonymous = result.right.is_anonymous || false;
    // Anonymous tokens last 4 hours, regular tokens last 5 minutes
    const expiresAt = Date.now() + (isAnonymous ? (4 * 60 * 60 * 1000) : (5 * 60 * 1000));

    console.log("JWT: Token fetched successfully", {
      tokenLength: token.length,
      isAnonymous,
      expiresAt: new Date(expiresAt).toISOString()
    });

    // Update the store with the new token
    singularityStore.setAiServer({
      ...singularityStore.aiServer,
      jwt: token,
      jwtExpiresAt: expiresAt,
      isAnonymousToken: isAnonymous
    });

    return token;
  }

  console.error("JWT: Failed to fetch JWT token");
  return null;
}

/**
 * Get a valid JWT token, refreshing if necessary
 */
export async function getValidJWT(): Promise<string | null> {
  if (!singularityStore.aiServer) {
    throw new Error("no FutureLayer aiServer");
  }
  
  console.log("JWT: Getting valid token");

  if (isJWTValid()) {
    console.log("JWT: Using cached token");
    return singularityStore.aiServer.jwt;
  }

  console.log("JWT: Token invalid or missing, fetching new token");
  return await fetchJWTToken();
}

/**
 * Fetch wrapper that automatically includes JWT authorization and base URL
 * @param endpoint - The endpoint path (e.g., "/ai/batch", "/unsplash")
 * @param options - Fetch options (body, signal, etc.)
 */
export async function fetchWithJWT(
  endpoint: string,
  options: Omit<RequestInit, "method"> & { body?: string; signal?: AbortSignal } = {}
): Promise<Response> {

  if (!singularityStore.aiServer) {
    throw new Error("no FutureLayer aiServer");
  }

  const jwt = await getValidJWT();
  if (!jwt) {
    throw new Error("Failed to get valid JWT token");
  }

  const url = getFutureLayerAppUrl(`/api/external${endpoint}`);
  const isAnonymous = singularityStore.aiServer.isAnonymousToken;

  console.log("JWT: Making authenticated fetch request", {
    url,
    isAnonymousToken: isAnonymous,
    tokenType: isAnonymous === true ? "ANONYMOUS (4hr)" : isAnonymous === false ? "SITE (5min)" : "UNKNOWN"
  });

  const response = await fetch(url, {
    method: "POST",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
      "Authorization": `Bearer ${jwt}`
    },
    signal: options.signal
  });

  // Extract and store site state from header if present (do this first, before any error handling)
  extractAndStoreSiteState(response);

  // Check for 401 unauthorized (expired anonymous JWT)
  if (response.status === 401) {
    console.error("JWT: Anonymous JWT expired or unauthorized");
    alert("Your session has expired. Please connect your site to FutureLayer to continue.");
  }

  // Check for 402 payment required (out of credits)
  if (response.status === 402) {
    const data = await response.json();

    if (data.error === "insufficient_credits") {
      console.error("JWT: Out of credits");
      singularityStore.setOutOfCreditsModalOpen(true);
      throw new Error("Insufficient credits");
    }

    if (data.error === "plan_expired") {
      console.error("JWT: Plan Expired");
      singularityStore.setPlanExpiredModalOpen(true);
      throw new Error("Plan expired");
    }

    // we should never get here
    alert('402 error in fetchWithJWT')
    throw new Error("402 error in fetchWithJWT");
  }

  return response;
}